import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { joinClassNames } from '../../util/util';
import { useConfiguration } from '../../contexts/ConfigurationContext';

import aiGeneratedBadge from '../../assets/images/ai-generated.png';
import aiModifiedBadge from '../../assets/images/ai-modified.png';
import aiTranscribedBadge from '../../assets/images/ai-transcribed.png';

import './AiTag.scss';

const AI_TAG_CONFIG = {
	'ai-generated': {
		badge: aiGeneratedBadge,
		labelKey: 'AI_GENERATED_LABEL',
		tooltipKey: 'AI_GENERATED_TOOLTIP',
	},
	'ai-transcribed': {
		badge: aiTranscribedBadge,
		labelKey: 'AI_TRANSCRIBED_LABEL',
		tooltipKey: 'AI_TRANSCRIBED_TOOLTIP',
	},
	'ai-modified': {
		badge: aiModifiedBadge,
		labelKey: 'AI_MODIFIED_LABEL',
		tooltipKey: 'AI_MODIFIED_TOOLTIP',
	},
} as const;

type AiTagValue = keyof typeof AI_TAG_CONFIG;

interface AiTagProps {
	style?: React.CSSProperties;
}

const AiTag: React.FC<AiTagProps> = ({ style }) => {
	const { t } = useTranslation();
	const { mediasData, currentMediaId } = useConfiguration();
	const [isHovered, setIsHovered] = useState(false);

	const currentMedia = useMemo(
		() => mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId),
		[mediasData, currentMediaId]
	);

	const aiTagValue = currentMedia?.custom?.['ai-tagged']?.toLowerCase() as AiTagValue | undefined;
	const tagConfig = aiTagValue ? AI_TAG_CONFIG[aiTagValue] : null;

	if (!tagConfig) {
		return null;
	}

	return (
		<div
			className="aiGeneratedTag"
			style={style}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<img className="aiGeneratedTag__badge" src={tagConfig.badge} alt={t(tagConfig.labelKey)} />
			<div
				aria-hidden="true"
				className={joinClassNames([
					'controlbar__control-tooltip',
					'controlbar__control-tooltip--light',
					isHovered ? 'controlbar__control-tooltip--visible' : '',
				])}
			>
				<div className="controlbar__control-tooltip-text">{t(tagConfig.tooltipKey)}</div>
			</div>
		</div>
	);
};

export default AiTag;
